/** cross-tab active-company sync. when tab A switches companies via
 *  useSwitchCompany, tab B's React Query cache and active route are
 *  stuck on the old company until B does something that re-reads the
 *  cookie. this module is the cure: tab A broadcasts, every other tab
 *  subscribed to the channel reacts by clearing its caches and forcing
 *  a socket reconnect (same effect as the local soft transition, minus
 *  the navigate since tab B chose its own route).
 *
 *  also handles the case where tab B was in the background while A
 *  switched: on visibilitychange to 'visible', we refetch the canonical
 *  pair ([me, companies] + [setup]) and let downstream invalidation
 *  cascade if either changed identity.
 */

const CHANNEL_NAME = 'tandemone.active_company'

/** companyId is null when the peer switched into Super, which has none.
 *  subscribers only invalidate and reconnect, so the payload is diagnostic. */
type ChannelMessage =
  | { type: 'switched'; companyId: string | null }
  | { type: 'created'; companyId: string }

/** lazy singleton. BroadcastChannel is a real handle the browser pools
 *  by name, so multiple subscribers in the same tab share one. */
let channel: BroadcastChannel | null = null

function ensureChannel(): BroadcastChannel | null {
  if (
    typeof window === 'undefined' ||
    typeof BroadcastChannel === 'undefined'
  ) {
    return null
  }
  if (!channel) channel = new BroadcastChannel(CHANNEL_NAME)
  return channel
}

/** publish that this tab switched/created. peer tabs pick it up via
 *  their subscribeActiveCompanyChannel call. no-op when BroadcastChannel
 *  is unavailable (older browsers, jsdom by default). */
export function postActiveCompanyChange(message: ChannelMessage): void {
  ensureChannel()?.postMessage(message)
}

/** subscribe a handler for peer-tab switches/creates. returns an
 *  unsubscribe function so the caller's useEffect cleanup can remove
 *  the listener on unmount. handler runs synchronously when the
 *  message lands, the caller is responsible for invalidating queries
 *  + reconnecting the socket (those side effects don't live here so
 *  this module stays a pure transport layer).
 */
export function subscribeActiveCompanyChannel(
  handler: (message: ChannelMessage) => void
): () => void {
  const bc = ensureChannel()
  if (!bc) return () => undefined
  function onMessage(event: MessageEvent<ChannelMessage>) {
    handler(event.data)
  }
  bc.addEventListener('message', onMessage)
  return () => bc.removeEventListener('message', onMessage)
}
